/* eslint-disable */ // no need to lint this file since it will be dropped
import {
  Component,
  ViewChild,
  Output,
  EventEmitter,
  Input,
  OnInit,
  OnDestroy,
  SimpleChanges,
  OnChanges
} from '@angular/core';
import { Operator } from './models/operator';
import { IChip } from './types';
import { Debounce, TimeoutWrapperService } from '@nrwl/common-lib';
import { AutoSuggestService } from './services/auto.suggest.service';
import { Chip } from './models/chip';
import { QueryExpressionParserService, SimpleChipExpression } from '@nrwl/query-emitter';
import { SmartQueryInput } from '../common.data.types';
import { first, get, last, trim } from 'lodash';
import { AutoSuggestComponent } from './auto.suggest/auto.suggest.component';
import { ISmartSearchLayoutPreferences } from '../smart.search/smart.search.component';

const placeholder = 'Enter filters here; filters can be applied to an app, site or location';

@Component({
  selector: 'free-form-filter',
  templateUrl: './free.form.filter.template.html',
  styleUrls: ['./free.form.filter.style.scss']
})
export class FreeFormFilterComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('input', { static: true }) inputField;
  @Input() queryExpression: string;
  @Input() treatEmptyAsValid: boolean;
  @Input() readonlyChipState: boolean;
  @Input() layoutPreferences: ISmartSearchLayoutPreferences;
  @Input() isAnalytics: boolean;
  @Output() inputChanged = new EventEmitter();
  @Output() inputFieldChangeEvent = new EventEmitter();
  @ViewChild(AutoSuggestComponent) autoSuggest: any;

  placeholder: string;
  chips: Array<IChip> = [];
  currentKeyword: string;
  currentOperator: string;
  constructor(
    private readonly queryEngine: QueryExpressionParserService,
    readonly autoSuggestService: AutoSuggestService, //not private 'cause used in template
    private readonly timeout: TimeoutWrapperService
  ) {}

  get useAutoSuggest(): boolean {
    return get(this.layoutPreferences, 'autoSuggest.enable', true);
  }

  get placeholderText(): string {
    return get(this.layoutPreferences, 'placeholder', placeholder);
  }

  get inputValue(): string {
    return this.inputField.nativeElement.value;
  }

  set inputValue(value: string) {
    this.inputField.nativeElement.value = value;
  }

  get hideFilterLabel() {
    return get(this.layoutPreferences, 'hideFilterLabel', false);
  }

  ngOnInit(): void {
    this.autoSuggestService.setRules(this.layoutPreferences.autoSuggest);
    this.parseQueryIntoChips();

    this.placeholder = this.chips.length ? '' : this.placeholderText;
  }

  ngOnDestroy(): void {
    this.timeout.onDestroy();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.layoutPreferences && !changes.layoutPreferences.isFirstChange()) {
      this.placeholder = this.chips.length ? '' : this.placeholderText;
    }
  }

  clearAll() {
    this.chips.length = 0;
    this.emitInputChanged();
  }

  /**
   * SIC! method is passed over the content from text input. It does not know anything about previously created chips,
   * that are stored in this.chips.
   */
  isChipExpressionValid(input: string): boolean {
    if (this.queryEngine.isChipExpressionValid(input)) {
      // be sure to pass over a list of all 'would be' chips.
      return this.chipsPassesBusinessLogicQuirks([
        ...this.chips,
        ...this.queryEngine.parseIntoTokens(this.inputValue)
      ]);
    }

    return false;
  }

  newChipPassesBusinessLogicQuirks(newChip): boolean {
    const allChips = this.chips.slice(0);
    allChips.push(newChip);
    return this.chipsPassesBusinessLogicQuirks(allChips);
  }

  chipsPassesBusinessLogicQuirks(asChips): boolean {
    return this.queryEngine.fitsWithUseCaseConstraints(asChips);
  }

  onKeyDown(event) {
    if (event.keyCode === 13) {
      let wildcardSearch = this.inputValue.charAt(0) === '*';
      if (wildcardSearch && this.isAnalytics) {
        this.inputValue = this.inputValue.replace(this.currentKeyword, this.currentKeyword + ' = ');
        this.inputValue = this.inputValue.slice(0, 0) + this.inputValue.slice(1);
      }
      const pieces: string[] = this.inputValue.split('=');
      if (pieces.length > 1) {
        this.inputValue = pieces.shift() + " = '" + trim(pieces.join('')) + "'";
      }
      if (this.isChipExpressionValid(this.inputValue)) {
        const chip = first(this.queryEngine.parseIntoTokens(this.inputValue));
        if (!chip.keyword || (chip.operands.length && chip.operands[0] === undefined)) {
          return;
        }
        this.addChip(new Chip(chip.keyword, this.currentOperator, chip.operands));
      }
      this.inputValue = '';
    }
  }

  @Debounce(200)
  onKeyUp(event) {
    const chip = this.queryEngine.parseChipIntoTokens(this.inputValue);
    let inputText = this.inputValue;
    if (chip && !this.newChipPassesBusinessLogicQuirks(chip)) {
      // ban input if autosuggestions would lead into content that does not pass business logic constraint.
      // (we should not suggest stuff and then later discard chip/disallow input)
      inputText = '';
    }
    this.autoSuggestService.set(inputText);

    this.inputFieldChangeEvent.emit({ event, value: this.inputValue });
  }

  isChip(chip: IChip) {
    return chip instanceof Chip;
  }

  isOperator(chip: IChip) {
    return chip instanceof Operator;
  }

  addChip(chip: Chip) {
    if (this.chips.length > 0) {
      this.chips.push(new Operator());
    }

    this.chips.push(chip);
    this.emitInputChanged();
  }

  //Remove chip, if needed operator too
  removeChip(chip: Chip) {
    let index = this.chips.indexOf(chip);
    if (index > 0) {
      index--;
    }
    this.chips.splice(index, 2);

    this.emitInputChanged();
  }

  onSuggestClick(item) {
    if (this.newChipPassesBusinessLogicQuirks(item)) {
      if (this.isAnalytics && item.tab === true && item.keyword.indexOf('*') === -1) {
        if (item.keyword !== 'keyword') {
          if (!item.operands[0]) {
            item.operands[0] = '   ';
          }
          this.addChip(item);
          this.inputValue = '';
        } else {
          this.inputValue = `${item.operands[0]} ${item.operator} `;
          this.inputField.nativeElement.focus();
        }
      } else {
        if (
          item.keyword !== 'keyword' &&
          item.operands &&
          item.operands.length > 0 &&
          item.operands[0].length > 0
        ) {
          this.addChip(item);
          this.inputValue = '';
        } else {
          if (item.operands && item.operands.length > 0 && item.operands[0].length > 0) {
            this.inputValue = `${item.operands[0]} ${item.operator} `;
          } else {
            this.currentKeyword = item.keyword;
            this.currentOperator = item.operator;
            this.inputValue = item.keyword + ' ';
          }
          this.inputField.nativeElement.focus();
        }
      }

      item.operator.indexOf('!') !== -1
        ? this.autoSuggestService.set(this.inputValue + ' !')
        : this.autoSuggestService.set(this.inputValue);

      this.inputFieldChangeEvent.emit({ event: null, value: this.inputValue });
    }
  }

  onArrowDown() {
    if (this.autoSuggest) {
      this.inputField.nativeElement.blur();
      this.autoSuggest.elementRef.nativeElement.focus();
    }
  }

  attemptAcceptExpression(triggerSearch?: boolean) {
    if (this.isExpressionValid(this.queryExpression)) {
      this.parseQueryIntoChips();
    }
    this.emitInputChanged(triggerSearch);
  }

  emitInputChanged(triggerSearch?: boolean) {
    this.queryExpression = this.queryEngine.toExpression(this.chips as SimpleChipExpression[]);
    const valid = this.isExpressionValid(this.queryExpression);
    const parsedQuery = this.queryEngine.parseIntoTokens(this.queryExpression);
    const smartQueryInput = new SmartQueryInput(
      this.queryExpression,
      valid,
      parsedQuery,
      triggerSearch
    );
    this.placeholder = this.chips.length ? '' : this.placeholderText;
    this.inputChanged.emit(smartQueryInput);
  }

  isExpressionValid(expression: string) {
    if (this.treatEmptyAsValid) {
      const trimmedExp = trim(expression);
      if (!trimmedExp.length) {
        return true;
      }
    }
    // empty expression is not valid
    return this.queryEngine.isExpressionValid(expression);
  }

  // only valid tokens are to be created
  parseQueryIntoChips() {
    this.chips = [];
    this.inputValue = '';

    const value = this.queryExpression.trim();

    if (value) {
      const tokens: IChip[] = this.queryEngine.parseIntoTokens(value);
      tokens.forEach((token: any) => {
        if (token.logicalOp) {
          const operator = new Operator(token.logicalOp);
          this.chips.push(operator);
        }
        const chip = new Chip(token.keyword, token.operator, token.operands);
        //only one chip should be read only
        if (this.readonlyChipState) {
          chip.readonly = this.readonlyChipState;
          this.readonlyChipState = false;
        }
        this.chips.push(chip);
      });
    }
  }

  getLastOperand(userInput): string {
    return userInput.operands
      ? Array.isArray(userInput.operands)
        ? last(userInput.operands)
        : userInput.operands
      : null;
  }

  enrichInputFromAutoselect(userInput, autocompleteTag) {
    if (userInput.keyword) {
      // no enrich.
      return;
    }

    if (autocompleteTag.dbInfo) {
      userInput.keyword = this.mapCategoryToKeyword(autocompleteTag.dbInfo.category);
      userInput.operator = '=';
      // assume the rest will be populated anyway
    }
  }

  mapCategoryToKeyword(category: string): string {
    return this.autoSuggestService.mapCategoryToKeyword(category);
  }
}
